import { useState, useEffect, useRef, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { Logo } from '@/components/ui/Logo'
import { FormField } from '@/components/ui/FormField'
import { getPwChecks, pwStrength, pwAllPass, pwStrengthLabel } from '@/lib/password'

export default function ResetPassword() {
  const navigate = useNavigate()
  const [password,      setPassword]      = useState('')
  const [confirm,       setConfirm]       = useState('')
  const [loading,       setLoading]       = useState(false)
  const [done,          setDone]          = useState(false)
  const [error,         setError]         = useState('')
  const [sessionReady,  setSessionReady]  = useState(false)
  const [linkExpired,   setLinkExpired]   = useState(false)
  const sessionReadyRef = useRef(false)
  const pwChecks = getPwChecks(password)
  const score    = pwStrength(pwChecks)
  const { label: strengthLabel, color: strengthColor } = pwStrengthLabel(score)

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION first, then PASSWORD_RECOVERY once code is exchanged
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' || (event === 'INITIAL_SESSION' && session)) {
        sessionReadyRef.current = true
        setSessionReady(true)
      }
    })

    // Fallback: if no session materialises within 10s, link is expired
    const timer = setTimeout(() => {
      if (!sessionReadyRef.current) setLinkExpired(true)
    }, 10000)

    return () => {
      subscription.unsubscribe()
      clearTimeout(timer)
    }
  }, [])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (loading) return
    if (!password.trim()) { setError('Password is required.'); return }
    if (!pwAllPass(getPwChecks(password))) { setError('Password does not meet all requirements.'); return }
    if (password !== confirm) { setError('Passwords do not match.'); return }
    setError('')
    setLoading(true)
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password })
      if (updateError) {
        const msg = updateError.message.toLowerCase()
        if (msg.includes('same') || msg.includes('different') || msg.includes('reuse')) {
          setError('Your new password must be different from your previous passwords.')
        } else if (msg.includes('expired') || msg.includes('invalid') || msg.includes('otp')) {
          setError('This reset link has expired. Please request a new one.')
        } else {
          setError(`Password reset failed: ${updateError.message}`)
        }
        return
      }
      await supabase.auth.signOut()
      setDone(true)
      setTimeout(() => navigate('/login', { replace: true }), 2000)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: '24px 16px', background: '#FAF6EF',
    }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 22 }}>
          <Logo size={60} onClick={() => navigate('/')} />
        </div>

        <div style={{
          background: '#fff', border: '1px solid rgba(27,42,74,0.08)',
          borderRadius: 18, padding: '28px 24px',
        }}>
          {done ? (
            <div className='banner-success' style={{ textAlign: 'center' }}>
              Password updated. Redirecting to log in…
            </div>
          ) : linkExpired ? (
            <>
              <div style={{ font: '600 22px Lora,serif', color: '#1B2A4A', textAlign: 'center' }}>
                Link expired
              </div>
              <div style={{ font: '400 13px Inter,sans-serif', color: 'rgba(27,42,74,0.55)', textAlign: 'center', marginTop: 8, marginBottom: 20 }}>
                This reset link has expired or already been used.
              </div>
              <button className='btn-primary' onClick={() => navigate('/forgot-password')}>
                Request a new link
              </button>
            </>
          ) : !sessionReady ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: 'rgba(27,42,74,0.45)', font: '400 14px Inter,sans-serif' }}>
              Verifying reset link…
            </div>
          ) : (
            <>
              <div style={{ font: '600 22px Lora,serif', color: '#1B2A4A', textAlign: 'center' }}>
                Set a new password
              </div>
              <div style={{ font: '400 13px Inter,sans-serif', color: 'rgba(27,42,74,0.55)', textAlign: 'center', marginTop: 5, marginBottom: 20 }}>
                Choose a strong password for your account
              </div>

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <FormField
                    label='New password'
                    type='password'
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder='Min 10 characters'
                    autoComplete='new-password'
                    required
                  />
                  {password.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
                        {[1,2,3,4,5].map(i => (
                          <div key={i} style={{
                            flex: 1, height: 4, borderRadius: 2,
                            background: i <= score ? strengthColor : 'rgba(27,42,74,0.1)',
                            transition: 'background 0.2s',
                          }} />
                        ))}
                        <div style={{ font: '500 11px Inter,sans-serif', color: strengthColor, flexShrink: 0, marginLeft: 4 }}>{strengthLabel}</div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {pwChecks.map(c => (
                          <div key={c.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{
                              width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                              background: c.pass ? '#0F7C6C' : 'rgba(27,42,74,0.12)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              font: '700 8px Inter,sans-serif', color: c.pass ? '#fff' : 'rgba(27,42,74,0.3)',
                            }}>✓</div>
                            <span style={{ font: '400 11.5px Inter,sans-serif', color: c.pass ? '#0F7C6C' : 'rgba(27,42,74,0.45)' }}>{c.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <FormField
                  label='Confirm new password'
                  type='password'
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  placeholder='Repeat your new password'
                  autoComplete='new-password'
                  required
                />
                {error && <div className='banner-error'>{error}</div>}
                <button type='submit' className='btn-primary' disabled={loading}>
                  {loading ? 'Saving…' : 'Set new password'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
